import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import classNames from 'classnames';
import range from 'lodash/range';
import { IconArrowHead, NamedLink } from '..';
import { stringify } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';

import css from './PaginationLinks.module.css';

const { string, object } = PropTypes;

/**
 * Returns an array containing page numbers and possible ellipsis '…' characters.
 *
 * @param {Number} page - current page
 * @param {Number} totalPages - total page count
 * @return {Array}
 */
const getPageNumbersArray = (page, totalPages) => {
  // Create array of numbers: [1, 2, 3, 4, { key: 0 }, totalPages]
  // A gap is represented by an object with a key because there can be multiple
  const numbersFrom1ToTotalPages = range(1, totalPages + 1);
  let gap = 0;

  return numbersFrom1ToTotalPages
    .filter(v => {
      // Filter numbers that are next to current page and pick also first and last page
      // E.g. [1, 4, 5, 6, 9], where current page = 5 and totalPages = 9.
      return v === 1 || Math.abs(v - page) <= 1 || v === totalPages;
    })
    .reduce((newArray, p) => {
      // Create a new array where gaps between consecutive numbers is filled with ellipsis character
      // E.g. [1, '…', 4, 5, 6, '…', 9], where current page = 5 and totalPages = 9.
      const isFirstPageOrNextToCurrentPage = p === 1 || newArray[newArray.length - 1] + 1 === p;

      // eslint-disable-next-line no-plusplus
      return isFirstPageOrNextToCurrentPage ? [...newArray, p] : [...newArray, { key: gap++ }, p];
    }, []);
};

/**
 * Component that renders "Previous page" and "Next page" pagination
 * links of the given page component with the given pagination
 * information.
 *
 * The links will be disabled when no previous/next page exists.
 */
export const PaginationLinksComponent = props => {
  const {
    className,
    rootClassName,
    intl,
    pageName,
    pagePathParams,
    pageSearchParams,
    pagination,
    textIntlId,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const { page, perPage, totalPages, totalItems } = pagination;
  const prevPage = page > 1 ? page - 1 : null;
  const nextPage = page < totalPages ? page + 1 : null;
  const prevSearchParams = { ...pageSearchParams, page: prevPage };
  const nextSearchParams = { ...pageSearchParams, page: nextPage };

  /* Arrow links: to previous page */
  const prevLinkEnabled = (
    <NamedLink
      className={css.prev}
      name={pageName}
      params={pagePathParams}
      to={{ search: stringify(prevSearchParams) }}
      title={intl.formatMessage({ id: 'PaginationLinks.previous' })}
    >
      <IconArrowHead direction="left" size="big" rootClassName={css.arrowIcon} />
    </NamedLink>
  );

  /* Arrow links: to next page */
  const nextLinkEnabled = (
    <NamedLink
      className={css.next}
      name={pageName}
      params={pagePathParams}
      to={{ search: stringify(nextSearchParams) }}
      title={intl.formatMessage({ id: 'PaginationLinks.next' })}
    >
      <IconArrowHead direction="right" size="big" rootClassName={css.arrowIcon} />
    </NamedLink>
  );

  /* Numbered pagination links */
  const pageNumbersNavLinks = getPageNumbersArray(page, totalPages).map(v => {
    const isCurrentPage = v === page;
    const pageClassNames = classNames(css.toPageLink, {
      [css.currentPage]: isCurrentPage,
      [css.notCurrentPage]: !isCurrentPage,
    });

    return typeof v === 'number' ? (
      <NamedLink
        key={v}
        className={pageClassNames}
        name={pageName}
        params={pagePathParams}
        to={{ search: stringify({ ...pageSearchParams, page: v }) }}
        title={intl.formatMessage({ id: 'PaginationLinks.toPage' }, { page: v })}
      >
        {v}
      </NamedLink>
    ) : (
      <div className={css.paginationGapWrapper} key={`pagination_gap_${v.key}`}>
        <span key={`pagination_gap_${v.key}`} className={css.paginationGap}>
          {'\u2026'}
        </span>
      </div>
    );
  });

  const pageNumberListClassNames = classNames(css.pageNumberList);
  const firstVisibleItem = page * perPage - (perPage - 1);
  const lastVisibleItem = page * perPage;

  return (
    <div className={classes}>
      <nav className={css.nav}>
        {prevPage ? prevLinkEnabled : null}
        <div className={pageNumberListClassNames}>{pageNumbersNavLinks}</div>
        {nextPage ? nextLinkEnabled : null}
      </nav>

      <div className={css.text}>
        {intl.formatMessage(
          { id: textIntlId },
          {
            firstVisibleItem,
            lastVisibleItem: lastVisibleItem > totalItems ? totalItems : lastVisibleItem,
            totalItems,
          }
        )}
      </div>
    </div>
  );
};

PaginationLinksComponent.defaultProps = {
  className: '',
  rootClassName: '',
  pagePathParams: {},
  pageSearchParams: {},
  textIntlId: 'PaginationLinks.paginationText',
};

PaginationLinksComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  pageName: string.isRequired,
  pagePathParams: object,
  pageSearchParams: object,
  pagination: propTypes.pagination.isRequired,
  textIntlId: string,
};

export default injectIntl(PaginationLinksComponent);
